import type { FC, ReactNode } from "react";
import { Table, TableBody, TableHead, TableHeadCell } from "flowbite-react";
import { HiArrowUp, HiArrowDown, HiSelector } from "react-icons/hi";
import type { SortState } from "../lib/sorting";
import { MetricTooltip } from "./sales-metrics/MetricTooltip";

export interface SortableTableColumn<T extends string = string> {
  key: T;
  label: string;
  sortable?: boolean;
  explanation?: string;
}

interface SortableColumnProps {
  label: string;
  sortKey: string;
  explanation?: string;
  currentSort: SortState<string>;
  onSort: (key: string) => void;
}

const SortableColumn: FC<SortableColumnProps> = ({
  label,
  sortKey,
  explanation,
  currentSort,
  onSort,
}) => {
  const isCurrentSort = currentSort.key === sortKey;
  const Icon = isCurrentSort
    ? currentSort.direction === "asc"
      ? HiArrowUp
      : HiArrowDown
    : HiSelector;

  return (
    <div
      className="flex cursor-pointer items-center gap-x-2"
      onClick={() => onSort(sortKey)}
    >
      <MetricTooltip title={label} explanation={explanation}>
        <span>{label}</span>
      </MetricTooltip>
      <Icon className="text-gray-400" />
    </div>
  );
};

interface SortableTableProps {
  columns: {
    key: string;
    label: string;
    sortable?: boolean;
    explanation?: string;
  }[];
  sortState: SortState<string>;
  onSort: (key: string) => void;
  /**
   * Drop the wrapper's shadow. Set this when the table already sits inside a card,
   * otherwise the two elevations stack and read as a card within a card.
   */
  flush?: boolean;
  children: ReactNode;
}

export const SortableTable: FC<SortableTableProps> = ({
  columns,
  sortState,
  onSort,
  flush = false,
  children,
}) => {
  return (
    <Table
      className="min-w-full divide-y divide-gray-200 dark:divide-gray-600 dark:bg-gray-800 dark:text-gray-400"
      theme={{
        root: {
          shadow: "hidden",
          wrapper: flush
            ? "relative overflow-x-auto sm:rounded-lg"
            : "relative overflow-x-auto shadow-md sm:rounded-lg",
        },
      }}
    >
      <TableHead className="bg-gray-100 dark:bg-gray-700">
        {columns.map((column) => (
          <TableHeadCell key={column.key}>
            {column.sortable ? (
              <SortableColumn
                label={column.label}
                sortKey={column.key}
                explanation={column.explanation}
                currentSort={sortState}
                onSort={onSort}
              />
            ) : (
              <MetricTooltip title={column.label} explanation={column.explanation}>
                <span>{column.label}</span>
              </MetricTooltip>
            )}
          </TableHeadCell>
        ))}
      </TableHead>
      <TableBody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
        {children}
      </TableBody>
    </Table>
  );
};
